import React, { useEffect, useState, useRef, useCallback } from 'react'
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setAdminAuth } from '../redux/authSlice';
import { RootState } from '../redux/store';
import Box from '@mui/material/Box';
import { useLocation } from "react-router-dom";
import { t } from 'i18next';
import LoginAdmin from './adminComponents/LoginAdmin';
import TabPanel from '@mui/lab/TabPanel';
import TabMenu from './util/TabMenu';
import PageToolbar from './util/PageToolbar';
import LoadingCenter from './util/LoadingCenter';
import EmptyState from './util/EmptyState';
import CompanyTable from './adminComponents/CompanyTable';
import CompanyCards from './adminComponents/CompanyCards';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun, faRightFromBracket, faTable, faTableCells } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import TariffTable from './adminComponents/TariffTable';
import NotificationTemplates from './adminComponents/NotificationTemplates';
import CountryDefaults from './adminComponents/CountryDefaults';
import { useSnackbar } from 'notistack';
import SidePanel from './util/SidePanel';
import AddTariff from './modal/AddTariff';
import AddCompany from './modal/AddCompany';
import AdminSetting from './modal/AdminSetting';
import {
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  Button,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AddCategoryHelp from './modal/AddCategoryHelp';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DeleteModalHelp from './modal/DeleteModalHelp';

interface AdminProps {
  baseUrl: any,
  isDarkTheme: boolean,
  setIsDarkTheme: (v: boolean) => void,
}

const Admin: React.FC<AdminProps> = ({ baseUrl, isDarkTheme, setIsDarkTheme }) => {
  const adminAuth = useSelector((state: RootState) => state.auth.admin);
  const [value, setValue] = useState('company');
  const [loading, setLoading] = useState(true);
  const [showAddTariff, setShowAddTariff] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);

  const [selected, setSelected] = useState<readonly number[]>([]);
  const [selectedTariff, setSelectedTariff] = useState<readonly number[]>([]);
  const [companyRow, setCompanyRow] = useState<any[]>([]);
  const [tariffRow, setTariffRow] = useState<any[]>([]);
  const [companyView, setCompanyView] = useState<'table' | 'cards'>('table');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [, setHelpData] = useState<any[]>([]);
  const [menuTree, setMenuTree] = useState<any[]>([]);
  const [selectedHelp, setSelectedHelp] = useState<any>(null);
  const [openMenus, setOpenMenus] = useState<Record<number, boolean>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [editedLayout, setEditedLayout] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const handleEdit = () => {
    setEditedLayout(selectedHelp.layout);
    setIsEditing(true);
  };

  const buildMenuTree = (data: any[]) => {
    const parents = data
      .filter(item => item.parentId === null)
      .sort((a, b) => a.id - b.id);

    return parents.map(parent => ({
      ...parent,
      children: data
        .filter(child => child.parentId === parent.id)
        .sort((a, b) => a.id - b.id)
    }));
  };

  const fetchHelpData = useCallback(async () => {
    try {
      const { data } = await axios.get('helpdesk');
      setHelpData(data);
      setMenuTree(buildMenuTree(data));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!adminAuth) return;
    fetchHelpData();
  }, [fetchHelpData, adminAuth]);

  useEffect(() => {
    setLoading(true);
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        dispatch(setAdminAuth(false));
        setLoading(false);
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // 1. Проверяем авторизацию — только этот запрос влияет на adminAuth
      try {
        await axios.get('admin/admin');
        dispatch(setAdminAuth(true));
      } catch (e) {
        console.error('[Admin] auth check failed:', e);
        dispatch(setAdminAuth(false));
        setLoading(false);
        return;
      }

      // 2. Грузим данные независимо — ошибки здесь не разлогинивают
      try {
        const { data: company } = await axios.get('company');
        setCompanyRow(Array.isArray(company) ? company : []);
      } catch (e) {
        console.error('[Admin] GET company failed:', e);
      }

      try {
        const { data: tariff } = await axios.get('tariff');
        setTariffRow(Array.isArray(tariff) ? tariff : []);
      } catch (e) {
        console.error('[Admin] GET tariff failed:', e);
      }

      setLoading(false);
    };
    checkAuth();
  }, [dispatch, location]);

  //refresh 
  const refreshCompany = async (back: string, set: any) => {
    try {
      const { data } = await axios.get(back);
      if (data) {
        set(data);
      }
    } catch (error: any) {
      console.log('error');
    }
  }

  const handleChangeAll = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const logout = async () => {
    try {
      await axios.post('admin/logout', {}, { withCredentials: true });
      dispatch(setAdminAuth(false));
      localStorage.removeItem('adminToken');
      setTimeout(() => {
        navigate('/adminpanel');
      }, 200);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  const handleCloseDrawer = (set: any) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    set(false);
  };

  useEffect(() => {
    localStorage.setItem('darkTheme', JSON.stringify(isDarkTheme));
    document.body.classList.toggle('dark-theme', isDarkTheme);
  }, [isDarkTheme]);

  const handleSave = async () => {
    if (!selectedHelp) return;
    try {
      await axios.put(`/helpdesk/${selectedHelp.id}`, { layout: editedLayout });
      setMenuTree(prev =>
        prev.map(item => {
          if (item.id === selectedHelp.id) return { ...item, layout: editedLayout };
          return {
            ...item,
            children: item.children.map((child: any) =>
              child.id === selectedHelp.id ? { ...child, layout: editedLayout } : child
            )
          };
        })
      );
      setSelectedHelp((prev: any) => prev && { ...prev, layout: editedLayout });
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (select:any) => {
    try {
      await axios.delete(`helpdesk/${select.id}`);
      setSelectedHelp(null);
      enqueueSnackbar('Delete success', { variant: 'success' });
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar('Delete error', { variant: 'error' });
    }
  };

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const resizeIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    iframe.style.height = "0px";
    iframe.style.height = doc.documentElement.scrollHeight + "px";
  };

  const adminToolbar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }}>
      <button
        type="button"
        className="btn-icon"
        title={isDarkTheme ? 'Light mode' : 'Dark mode'}
        onClick={() => setIsDarkTheme(!isDarkTheme)}>
        <FontAwesomeIcon icon={isDarkTheme ? faSun : faMoon} />
      </button>
      <button
        type="button"
        className="dt-btn-ghost"
        onClick={logout}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <FontAwesomeIcon icon={faRightFromBracket} />
        <span>{t('Logout')}</span>
      </button>
    </div>
  );

  return (
    <>
      {adminAuth ?
        (
          <Box sx={{ width: '100%', typography: 'body1', padding: '0 20px' }}>
            <TabMenu value={value} onChange={handleChangeAll} tabs={[
              { label: 'Company',  value: 'company' },
              { label: 'Tariff',   value: 'tariff' },
              { label: 'Payments', value: 'payments' },
              { label: 'Settings', value: 'settings' },
              { label: 'Notifications', value: 'notifications' },
              { label: 'Countries', value: 'countries' },
              { label: 'Help',     value: 'help' },
            ]} rightElement={adminToolbar}>

              {/* ── COMPANY ── */}
              <TabPanel value='company' sx={{ padding: 0 }}>
                <PageToolbar>
                  <button type="button" className="btn-primary" onClick={() => setShowAddCompany(true)}>
                    + {t('AddCompany')}
                  </button>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      className={companyView === 'table' ? 'btn-primary' : 'dt-btn-ghost'}
                      title="Table view"
                      onClick={() => setCompanyView('table')}>
                      <FontAwesomeIcon icon={faTable} />
                    </button>
                    <button
                      type="button"
                      className={companyView === 'cards' ? 'btn-primary' : 'dt-btn-ghost'}
                      title="Card view"
                      onClick={() => setCompanyView('cards')}>
                      <FontAwesomeIcon icon={faTableCells} />
                    </button>
                  </div>
                </PageToolbar>
                {loading ? <LoadingCenter /> : companyView === 'table' ? (
                  <CompanyTable
                    selected={selected}
                    setSelected={setSelected}
                    baseUrl={baseUrl}
                    companyRow={companyRow}
                    tariffRow={tariffRow}
                    refreshCompany={() => refreshCompany('company', setCompanyRow)}
                  />
                ) : (
                  <CompanyCards
                    companyRow={companyRow}
                    tariffRow={tariffRow}
                    baseUrl={baseUrl}
                    onRefresh={() => refreshCompany('company', setCompanyRow)}
                  />
                )}
              </TabPanel>

              {/* ── TARIFF ── */}
              <TabPanel value='tariff' sx={{ padding: 0 }}>
                <PageToolbar>
                  <button type="button" className="btn-primary" onClick={() => setShowAddTariff(true)}>
                    + {t('AddTariff')}
                  </button>
                </PageToolbar>
                {loading ? <LoadingCenter /> : (
                  <TariffTable
                    selected={selectedTariff}
                    setSelected={setSelectedTariff}
                    companyRow={tariffRow}
                    refreshCompany={() => refreshCompany('tariff', setTariffRow)}
                  />
                )}
              </TabPanel>

              {/* ── PAYMENTS ── */}
              <TabPanel value='payments' sx={{ padding: 0 }}>
                <EmptyState icon="💳" message="Stripe integration is disabled" sub="Payment history will appear here once Stripe is enabled" />
              </TabPanel>

              {/* ── SETTINGS ── */}
              <TabPanel value='settings' sx={{ padding: 0 }}>
                <AdminSetting isTab />
              </TabPanel>

              {/* ── NOTIFICATIONS ── */}
              <TabPanel value='notifications' sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '75vh', minHeight: 0 }}>
                  <NotificationTemplates baseUrl={baseUrl} />
                </div>
              </TabPanel>

              {/* ── COUNTRIES ── */}
              <TabPanel value='countries' sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '75vh', minHeight: 0 }}>
                  <CountryDefaults />
                </div>
              </TabPanel>

              {/* ── HELP ── */}
              <TabPanel value='help' sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="box-users flex flex-col justify-start rounded-[5px] mb-[15px] h-full">
                  <div className='admin-box-users'>
                    <PageToolbar>
                      <button type="button" className="btn-primary" onClick={() => setOpenAddCategory(true)}>
                        + {t('AddCategory')}
                      </button>
                    </PageToolbar>
                    <Box sx={{ display: "flex", flex: 1, height: "100%", minHeight: 0 }}>
                      {/* LEFT MENU */}
                      <Box sx={{ width: 200, overflowY: "auto", background: "var(--bg-header-fix-menu)" }}>
                        <List component="nav">
                          {menuTree.map(item => (
                            <React.Fragment key={item.id}>
                              <ListItemButton
                                onClick={() => {
                                  setOpenMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                                  setSelectedHelp(item);
                                }}
                                selected={selectedHelp?.id === item.id}
                              >
                                <ListItemText primary={item.title} />
                                {item.children.length > 0 && (openMenus[item.id] ? <ExpandLess /> : <ExpandMore />)}
                              </ListItemButton>

                              {item.children.length > 0 && (
                                <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
                                  <List component="div" disablePadding>
                                    {item.children.map((child: any) => (
                                      <ListItemButton
                                        key={child.id}
                                        sx={{ pl: 3 }}
                                        onClick={() => setSelectedHelp(child)}
                                        selected={selectedHelp?.id === child.id}
                                      >
                                        <ListItemText primary={child.title} primaryTypographyProps={{ fontSize: "0.8rem" }} />
                                      </ListItemButton>
                                    ))}
                                  </List>
                                </Collapse>
                              )}
                            </React.Fragment>
                          ))}
                        </List>
                      </Box>

                      {/* DIVIDER */}
                      <div className="border-l-2 hr mr-[10px] h-full" />

                      {/* RIGHT CONTENT */}
                      <Box sx={{ flex: 1, pt: 2, pr: 1, mb: 3, overflowY: "auto", minHeight: 0 }}>
                        {selectedHelp ? (
                          <>
                            {/* Edit / Save / Delete / Cancel */}
                            <div style={{ display: 'flex', gap: 'var(--space-l3)', marginBottom: 12 }}>
                              {!isEditing ? (
                                <>
                                  <button type="button" className="btn-primary" onClick={handleEdit}>{t('Edit')}</button>
                                  <button type="button" className="dt-btn-ghost" style={{ color: 'var(--color-danger, #dc2626)' }} onClick={() => setShowDelete(true)}>{t('Delete')}</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" className="btn-primary" onClick={handleSave}>{t('Save')}</button>
                                  <button type="button" className="dt-btn-ghost" onClick={() => setIsEditing(false)}>{t('Cancel')}</button>
                                </>
                              )}
                            </div>
                            {/* Editor */}
                            {isEditing ? (
                              <ReactQuill
                                theme="snow"
                                value={editedLayout}
                                onChange={setEditedLayout}
                                modules={{
                                  toolbar: [
                                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                    [{ size: [] }],
                                    ["bold", "italic", "underline", "strike"],
                                    [{ color: [] }],
                                    [{ align: [] }],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    ["link", "image", "video"],
                                    ["clean"]
                                  ]
                                }}
                              />
                            ) : (
                              <iframe
                                ref={iframeRef}
                                title="Help Preview"
                                onLoad={resizeIframe}
                                style={{
                                  width: "100%",
                                  border: "none",
                                  height: '80vh',
                                  display: "block"
                                }}
                                srcDoc={`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            body {
              margin: 0;
              font-family: Arial, sans-serif;
            }
          </style>
        </head>
        <body>
          ${selectedHelp.layout}
        </body>
      </html>
    `}
                              />
                            )}
                          </>
                        ) : (
                          <Typography sx={{ color: 'var(--text-color-calendar-week)', fontSize: 13 }}>Select category</Typography>
                        )}
                      </Box>
                    </Box>
                  </div>
                </div>
              </TabPanel>
            </TabMenu>
            <SidePanel
                            open={showAddTariff}
              onClose={() => setShowAddTariff(false)}>
              <AddTariff
                setShowAddTariff={setShowAddTariff}
                refreshTariff={() => refreshCompany('tariff', setTariffRow)}
              />
            </SidePanel>
            <SidePanel
                            open={showAddCompany}
              onClose={() => setShowAddCompany(false)}>
              <AddCompany
                setShowAddCompany={setShowAddCompany}
                refreshCompany={() => refreshCompany('company', setCompanyRow)}
              />
            </SidePanel>
            <SidePanel
                            open={openAddCategory}
              onClose={() => setOpenAddCategory(false)}>
              <AddCategoryHelp
                setOpenAddCategory={setOpenAddCategory}
                fetchHelpData={fetchHelpData}
              />
            </SidePanel>
            {showDelete &&
              <DeleteModalHelp
                open={showDelete}
                setOpen={setShowDelete}
                handleDeleteReport={handleDelete}
                selectedDateId={selectedHelp}
                updateReports={fetchHelpData} />
            }
          </Box>
        ) : (
          <LoginAdmin />)}
    </>
  )
}

export default Admin
